/**
 * Release-note lines in every language (the user, 2026-10-28: "What's new in
 * MyRave should work as a template — the what's new just needs to FLAG the
 * line, and the text should come from templates").
 *
 * A release FLAGS a line by writing it, in English, under a section of its
 * `releases/<version>.md`. The words people read come from a TEMPLATE:
 * `releases/i18n/<language>.json`, one table per language, keyed by the
 * English line exactly — the house rule of `tr()` ("the English text IS the
 * key"). English has no table: it is the keys. A language that lacks a line
 * reads the English, never nothing.
 *
 * ONE copy, read by both sides: the web app loads a language's table lazily
 * on the pages that show release notes (`frontend/src/lib/releaseLines.ts`),
 * and a deploy sends the server the translations of that release's own lines
 * (`scripts/release.mts record` adds `translations`), so the "MyRave news" notification is
 * written in each recipient's language without the Worker carrying every
 * table. `tests/src/releaseTemplates.test.ts` holds every flagged line to a
 * template in every language, and every template to a flagged line.
 */

/** One language's templates: English line → that language's words. */
export type ReleaseTemplates = Readonly<Record<string, string>>;

/** The `code spans` of a line — a template keeps the same ones, unchanged. */
export function codeSpans(line: string): string[] {
  return (line.match(/`[^`]+`/g) ?? []).sort();
}

/** `{placeholders}` of a line — a template keeps the same ones. */
export function placeholders(line: string): string[] {
  return (line.match(/\{[A-Za-z_]\w*\}/g) ?? []).sort();
}

/** The line in a language: its template, else the English as flagged. */
export function releaseLine(english: string, templates: ReleaseTemplates | null | undefined): string {
  const t = templates?.[english];
  return typeof t === 'string' && t.trim() ? t : english;
}

/**
 * The translations of `lines`, line by line — `{ english: { he: '…', … } }` —
 * for the languages that have one. What a deploy sends with its release.
 */
export function lineTranslations(
  lines: readonly string[],
  tables: Readonly<Record<string, ReleaseTemplates>>,
): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const line of lines) {
    const local: Record<string, string> = {};
    for (const [language, table] of Object.entries(tables)) {
      const t = table[line];
      if (typeof t === 'string' && t.trim()) local[language] = t;
    }
    if (Object.keys(local).length > 0) out[line] = local;
  }
  return out;
}

/**
 * What is wrong between the flagged lines and one language's templates: a
 * line with no template (or an empty one), a template that dropped or changed
 * a code span or a placeholder, and a template no release flags (an orphan —
 * the English it was keyed by was reworded, so nobody reads it).
 */
export function templateProblems(flagged: readonly string[], templates: ReleaseTemplates): {
  missing: string[]; changed: string[]; orphans: string[];
} {
  const want = new Set(flagged);
  const missing: string[] = [];
  const changed: string[] = [];
  for (const line of want) {
    const t = templates[line];
    if (typeof t !== 'string' || !t.trim()) { missing.push(line); continue; }
    if (codeSpans(t).join('|') !== codeSpans(line).join('|') || placeholders(t).join('|') !== placeholders(line).join('|')) changed.push(line);
  }
  const orphans = Object.keys(templates).filter((k) => !want.has(k));
  return { missing, changed, orphans };
}
